import type { HouseType } from '../types/house'
import type { Product } from '../types/product'
import type { Season, UsageProfile } from '../types/scan'

export const KWH_PER_PANEL = 340
export const VALUE_PER_STORED_KWH = 0.29
export const TRADING_SPREAD = 0.1
export const PRICE_GROWTH = 0.03
export const HEAT_PUMP_KWH = 2800
export const EV_KWH = 2500
export const AIRCO_KWH = 250

const BASE_USAGE = [0, 1800, 2750, 3350, 3900, 4600]

const HOUSE_FACTOR: Record<HouseType, number> = {
  vrijstaand: 1.15,
  'twee-onder-een-kap': 1.07,
  hoekwoning: 1.03,
  tussenwoning: 1,
  appartement: 0.85,
}

// Relative monthly solar yield (Jan → Dec) for the Netherlands.
const MONTH_SOLAR = [0.3, 0.5, 0.85, 1.25, 1.5, 1.55, 1.5, 1.3, 1.0, 0.65, 0.35, 0.25]
const MONTH_SUM = MONTH_SOLAR.reduce((s, v) => s + v, 0)

export function estimateUsage(u: Pick<UsageProfile, 'houseType' | 'persons' | 'heatPump' | 'evCharger' | 'airco'>): number {
  const base = BASE_USAGE[Math.min(Math.max(u.persons, 1), 5)] * HOUSE_FACTOR[u.houseType]
  const extra = (u.heatPump ? HEAT_PUMP_KWH : 0) + (u.evCharger ? EV_KWH : 0) + (u.airco ? AIRCO_KWH : 0)
  return Math.round((base + extra) / 50) * 50
}

export interface Advice {
  product: Product
  annualSolar: number
  selfUseWithout: number
  stored: number
  savingsSelf: number
  savingsTrading: number
  savingsTotal: number
  paybackYears: number
  autarkyWithout: number
  autarkyWith: number
  co2Kg: number
  netBenefit15: number
}

export function computeAdvice(usage: UsageProfile, product: Product): Advice {
  const panels = usage.hasSolar ? usage.solarPanels : 0
  const annualSolar = panels * KWH_PER_PANEL
  const selfUseWithout = Math.min(annualSolar * 0.3, usage.annualUsage * 0.35)
  const surplus = annualSolar - selfUseWithout
  const remainingDemand = usage.annualUsage - selfUseWithout
  const usable = product.capacity * 0.9

  let stored = 0
  for (const f of MONTH_SOLAR) {
    const dailySurplus = (surplus / 365) * ((f * 12) / MONTH_SUM) * 0.9
    const dailyEveningDemand = (remainingDemand / 365) * 0.65
    stored += 30.4 * Math.min(dailySurplus, usable, dailyEveningDemand)
  }
  stored = Math.max(0, stored)

  const savingsSelf = stored * VALUE_PER_STORED_KWH
  const tradingCycles = usage.contract === 'dynamisch' ? 200 : 0
  const savingsTrading = usable * tradingCycles * TRADING_SPREAD * (annualSolar > 0 ? 0.7 : 1)
  const savingsTotal = savingsSelf + savingsTrading
  const paybackYears = savingsTotal > 0 ? product.price / savingsTotal : Infinity

  let cumulative = 0
  for (let y = 0; y < 15; y++) cumulative += savingsTotal * Math.pow(1 + PRICE_GROWTH, y)

  return {
    product,
    annualSolar,
    selfUseWithout,
    stored,
    savingsSelf,
    savingsTrading,
    savingsTotal,
    paybackYears,
    autarkyWithout: usage.annualUsage > 0 ? (selfUseWithout / usage.annualUsage) * 100 : 0,
    autarkyWith: usage.annualUsage > 0 ? ((selfUseWithout + stored) / usage.annualUsage) * 100 : 0,
    co2Kg: (stored + (tradingCycles > 0 ? usable * tradingCycles * 0.3 : 0)) * 0.33,
    netBenefit15: cumulative - product.price,
  }
}

export function recommendProduct(usage: UsageProfile, products: Product[]): Product {
  return products.reduce((best, p) =>
    computeAdvice(usage, p).netBenefit15 > computeAdvice(usage, best).netBenefit15 ? p : best,
  )
}

export interface PaybackPoint {
  year: number
  besparing: number
  investering: number
}

export function paybackSeries(advice: Advice, years = 15): PaybackPoint[] {
  const points: PaybackPoint[] = [{ year: 0, besparing: 0, investering: advice.product.price }]
  let cumulative = 0
  for (let y = 1; y <= years; y++) {
    cumulative += advice.savingsTotal * Math.pow(1 + PRICE_GROWTH, y - 1)
    points.push({ year: y, besparing: Math.round(cumulative), investering: advice.product.price })
  }
  return points
}

export interface DayPoint {
  hour: number
  label: string
  solar: number
  load: number
  battery: number
  soc: number
  grid: number
}

function loadShape(h: number): number {
  if (h < 6) return 0.55
  if (h < 9) return 1.25
  if (h < 16) return 0.8
  if (h < 17) return 1.1
  if (h < 21) return 1.9
  return 0.9
}

const pad = (n: number) => String(n).padStart(2, '0')

export function simulateDay(
  usage: Pick<UsageProfile, 'hasSolar' | 'solarPanels' | 'annualUsage' | 'contract'>,
  capacity: number,
  maxPower: number,
  season: Season,
  step = 1,
): DayPoint[] {
  const panels = usage.hasSolar ? usage.solarPanels : 0
  const peak = panels * 0.4 * (season === 'zomer' ? 0.8 : 0.22)
  const [start, end] = season === 'zomer' ? [5.5, 21] : [8.5, 16.5]
  const avgLoad = (usage.annualUsage / 8760) * (season === 'winter' ? 1.2 : 0.85)
  const minSoc = capacity * 0.05
  let soc = capacity * (season === 'zomer' ? 0.35 : 0.12)
  const points: DayPoint[] = []

  for (let h = 0; h < 24 - 1e-9; h += step) {
    const mid = h + step / 2
    const solar = mid > start && mid < end ? peak * Math.sin((Math.PI * (mid - start)) / (end - start)) : 0
    const load = avgLoad * loadShape(mid)
    const net = solar - load
    let battery =
      net > 0 ? Math.min(net, maxPower, (capacity - soc) / step) : -Math.min(-net, maxPower, (soc - minSoc) / step)

    // Dynamic contract: top up from the grid during cheap night hours.
    if (usage.contract === 'dynamisch' && mid >= 2 && mid < 5) {
      const target = capacity * (season === 'winter' ? 0.85 : 0.45)
      const extra = Math.min(maxPower - Math.max(battery, 0), (target - soc) / step)
      if (extra > 0) battery = Math.max(battery, 0) + extra
    }

    soc = Math.min(capacity, Math.max(0, soc + battery * step))
    const minutes = Math.round((h % 1) * 60)
    points.push({
      hour: h,
      label: `${pad(Math.floor(h))}:${pad(minutes)}`,
      solar: round(solar),
      load: round(load),
      battery: round(battery),
      soc: Math.round((soc / capacity) * 100),
      grid: round(load + battery - solar),
    })
  }
  return points
}

function round(n: number): number {
  return Math.round(n * 100) / 100
}
